import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { patch } from '@truefit/http-utils';
import {
  usePlayByPlayClipGroupsQuery,
  useCreateClipGroupMutation,
  useDeleteClipGroupMutation,
  useAddClipToGroupMutation,
  useResetClipGroupMutation,
  useUpdateClipGroupMutation,
} from '@/features/clipGroups/hooks';
import useEnqueueClipForExportMutation from '@/features/clips/hooks/useEnqueueClipForExportMutation';
import { ClipGroupModel } from '@/features/clipGroups/types';
import { AUTO_DIRECTOR_CAMERA, ClipCameraSelection, ClipStatus } from '@/features/clips/types';
import { useSnackbar } from 'notistack';
import Tracking from '@/shared/services/tracking';
import { SportEvent } from '../types/SportEventTypes';
import { getEventPayloadDescription, secondsToTimeString } from '../utils/sportEventUtils';
import { decodePeriodDisplay } from '@/shared/utils/getPeriodName';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';
import { markExportRequested } from '../hooks/clipDownloadFeedback';

// Draft clipGroup type (before persisted to DB)
interface DraftClipGroup {
  id: string;
  name: string;
  trackedObjectId: string | null;
  inTime: Date;
  outTime: Date;
  isPersisted: false;
  isCreating?: boolean; // Track if this draft is being created in backend
  persistedId?: number; // Store the backend ID once created
  // When true, the draft is NOT auto-persisted on tracked-object selection;
  // it only persists when the user creates the first clip (camera click).
  // Used for the "Download shift" flow where opening the panel must not
  // produce backend state until the user actually exports a clip.
  persistOnExportOnly?: boolean;
  outputMode?: 'portrait' | 'landscape'; // Output transcoding profile preference
}

interface AddDraftClipGroupOverrides {
  name?: string;
  trackedObjectId?: string | null;
  inTime?: Date;
  outTime?: Date;
  persistOnExportOnly?: boolean;
  outputMode?: 'portrait' | 'landscape';
}

// Fixed clip data for save operations
interface FixedClipData {
  cameraId: number;
  pan: number;
  tilt: number;
  zoom: number;
  /** Frame with this preset; the API resolves pan/tilt/zoom from it. */
  framingPresetId?: number;
}

/**
 * What `createClipInGroup` actually did. Callers that track clip creation must
 * emit only on 'created' — the other outcomes cover a re-export of an existing
 * clip, and the several paths that bail out without writing anything.
 */
export type ClipCreationOutcome = 'created' | 'requeued' | 'aborted';

interface MediaContextValue {
  // State
  clipGroups: (ClipGroupModel | DraftClipGroup)[];
  selectedEventId: string | null;
  isLoading: boolean;
  isSavingFixedClip: boolean;

  // Actions
  setSelectedEvent: (event: SportEvent | null) => void;
  updateClipGroupTime: (
    groupId: string | number,
    field: 'inTime' | 'outTime',
    time: Date,
  ) => Promise<void>;
  addDraftClipGroup: (overrides?: AddDraftClipGroupOverrides) => string;
  updateDraftTrackedObject: (draftId: string, trackedObjectId: string | null) => Promise<void>;
  updateTrackedObject: (groupId: string | number, trackedObjectId: string | null) => Promise<void>;
  updateDraftOutputMode: (draftId: string, outputMode: 'portrait' | 'landscape') => void;
  createClipInGroup: (
    groupId: string | number,
    camera: ClipCameraSelection,
  ) => Promise<ClipCreationOutcome>;
  deleteClipGroup: (groupId: string | number) => Promise<void>;
  refetchClipGroups: () => Promise<void>;
  reset: () => void;

  // Fixed camera clip actions
  /** Resolves whether the position was saved (a failure shows its own snackbar). */
  saveFixedClip: (clipGroupId: number, data: FixedClipData) => Promise<boolean>;
  triggerFixedClipExport: (clipId: number) => Promise<void>;
}

const MediaContext = createContext<MediaContextValue | undefined>(undefined);

export const MediaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Game context for period naming mapping
  const gameContext = useCurrentGame();
  const periodNamingMapping = gameContext?.gameTypeMetadata?.periodNamingMapping?.[0];

  // State
  const [selectedEvent, setSelectedEventState] = useState<SportEvent | null>(null);
  const [draftClipGroups, setDraftClipGroups] = useState<DraftClipGroup[]>([]);
  const [isSavingFixedClip, setIsSavingFixedClip] = useState(false);

  // Derived values from selected event (using 'any' cast due to protobuf quoted property names).
  // event.id is `string` per the SES proto; numeric SES events are numeric strings,
  // shifts use composite strings like "shift_14037_1".
  const eventData = selectedEvent as any;
  const selectedEventId: string | null = eventData?.id != null ? String(eventData.id) : null;
  const eventTimestamp = selectedEvent?.timestamp ? new Date(selectedEvent.timestamp) : null;
  const eventType =
    typeof eventData?.payload === 'string' ? eventData.payload : eventData?.payload?.type || null;
  const eventPeriod = eventData?.clock?.period ?? null;
  const eventGameTime = (eventData?.clock?.time as number) ?? null;

  // Fetch persisted clipGroups from API
  const {
    clipGroups: persistedClipGroups = [],
    isLoading,
    refetch,
  } = usePlayByPlayClipGroupsQuery({
    playbyplayId: selectedEventId || '',
    enabled: !!selectedEventId,
  });

  // Mutations
  const createClipGroupMutation = useCreateClipGroupMutation();
  const deleteClipGroupMutation = useDeleteClipGroupMutation();
  const addClipToGroupMutation = useAddClipToGroupMutation();
  const resetClipGroupMutation = useResetClipGroupMutation();
  const updateClipGroupMutation = useUpdateClipGroupMutation();
  const enqueueClipForExportMutation = useEnqueueClipForExportMutation();

  // Auto-cleanup: Remove drafts that have been persisted once they appear in query results
  useEffect(() => {
    if (persistedClipGroups.length === 0) return;

    setDraftClipGroups((prev) => {
      const persistedIds = new Set(persistedClipGroups.map((g) => g.id));
      return prev.filter((draft) => {
        // Keep draft if it doesn't have a persistedId yet, or if it's not in the persisted list yet
        return !draft.persistedId || !persistedIds.has(draft.persistedId);
      });
    });
  }, [persistedClipGroups]);

  // Combine persisted + draft clipGroups
  // Filter out drafts that have been persisted (to avoid duplicates during transition)
  const clipGroups = useMemo(() => {
    const persistedIds = new Set(persistedClipGroups.map((g) => g.id));
    const visibleDrafts = draftClipGroups.filter(
      (draft) => !draft.persistedId || !persistedIds.has(draft.persistedId),
    );
    return [...persistedClipGroups, ...visibleDrafts];
  }, [persistedClipGroups, draftClipGroups]);

  // Set selected event - stores the full event object
  const setSelectedEvent = useCallback((event: SportEvent | null) => {
    setSelectedEventState(event);
  }, []);

  // Add new draft clipGroup. Defaults to event time ± 5s for in/out and
  // null trackedObjectId; callers (like ShiftClipView) can override any field.
  const addDraftClipGroup = useCallback(
    (overrides?: AddDraftClipGroupOverrides) => {
      const hasExplicitTimes = !!overrides?.inTime && !!overrides?.outTime;
      if (!eventTimestamp && !hasExplicitTimes) {
        console.warn('Cannot add clipGroup: no event timestamp and no explicit times');
        return '';
      }

      const enrichedName =
        overrides?.name?.trim() ||
        getEventPayloadDescription(
          selectedEvent ?? undefined,
          gameContext?.game?.season?.seasonType,
        )?.trim() ||
        'Unnamed Event';

      Tracking.getInstance().track('New ClipGroup', { category: 'Clips', name: enrichedName });

      const eventTime = eventTimestamp ? new Date(eventTimestamp) : new Date();
      const newDraftGroup: DraftClipGroup = {
        id: `draft-${Date.now()}`,
        name: enrichedName,
        trackedObjectId: overrides?.trackedObjectId ?? null,
        inTime: overrides?.inTime ?? new Date(eventTime.getTime() - 5000),
        outTime: overrides?.outTime ?? new Date(eventTime.getTime() + 5000),
        isPersisted: false,
        persistOnExportOnly: overrides?.persistOnExportOnly,
      };
      setDraftClipGroups((prev) => [...prev, newDraftGroup]);
      return newDraftGroup.id;
    },
    [eventTimestamp, selectedEvent, gameContext?.game?.season?.seasonType],
  );

  // Update clipGroup time (works for both drafts and persisted clipGroups)
  const updateClipGroupTime = useCallback(
    async (groupId: string | number, field: 'inTime' | 'outTime', time: Date) => {
      // Check if it's a draft
      if (typeof groupId === 'string' && groupId.startsWith('draft-')) {
        setDraftClipGroups((prev) =>
          prev.map((group) => (group.id === groupId ? { ...group, [field]: time } : group)),
        );
        return;
      }

      Tracking.getInstance().track('Update Clip Group Time', { category: 'Clips' });

      // For persisted clipGroups, verify it exists
      const persistedGroup = persistedClipGroups.find((g) => g.id === groupId);
      if (!persistedGroup || !selectedEventId) {
        return;
      }

      // Always use reset endpoint when changing times - it handles both cases:
      // - If clips exist: deletes them and updates times
      // - If no clips exist: just updates times
      // This ensures clips are always regenerated with the new timing
      try {
        const timeData =
          field === 'inTime' ? { inTime: time.toISOString() } : { outTime: time.toISOString() };

        await resetClipGroupMutation.mutateAsync({
          playbyplayId: selectedEventId,
          clipGroupId: groupId as number,
          ...timeData,
        });

        // Force refetch to get updated times from backend
        await refetch();
      } catch (error: any) {
        enqueueSnackbar(`Failed to update time: ${error.message}`, { variant: 'error' });
      }
    },
    [persistedClipGroups, selectedEventId, resetClipGroupMutation, refetch, enqueueSnackbar],
  );

  // Update tracked object for draft clipGroup
  // When a trackedObjectId is selected, immediately create the clipGroup in the backend
  const updateDraftTrackedObject = useCallback(
    async (draftId: string, trackedObjectId: string | null) => {
      if (!selectedEventId) {
        console.warn('Cannot create clipGroup: missing selectedEventId');
        return;
      }
      Tracking.getInstance().track('Update Tracked Object', { category: 'Clips', draft: true });

      // Find the draft clipGroup to get its times
      const draftGroup = draftClipGroups.find((g) => g.id === draftId);
      if (!draftGroup) {
        console.warn('Draft clipGroup not found:', draftId);
        return;
      }

      // If trackedObjectId is null, just update local state (don't create in backend)
      if (trackedObjectId === null) {
        setDraftClipGroups((prev) =>
          prev.map((group) => (group.id === draftId ? { ...group, trackedObjectId } : group)),
        );
        return;
      }

      // Drafts marked persistOnExportOnly (e.g. shift downloads) defer
      // persistence to first-clip-creation; just update local state here.
      if (draftGroup.persistOnExportOnly) {
        setDraftClipGroups((prev) =>
          prev.map((group) => (group.id === draftId ? { ...group, trackedObjectId } : group)),
        );
        return;
      }

      // Set loading state
      setDraftClipGroups((prev) =>
        prev.map((group) =>
          group.id === draftId ? { ...group, trackedObjectId, isCreating: true } : group,
        ),
      );

      try {
        // Create clipGroup in backend with trackedObjectId and this draft's specific times
        // Clean and sanitize the name/description
        const cleanedName = draftGroup.name?.trim() || 'Unnamed Event';

        const createData: Record<string, unknown> = {
          name: cleanedName,
          trackedObjectId,
          inTime: draftGroup.inTime.toISOString(),
          outTime: draftGroup.outTime.toISOString(),
        };

        // Only include optional fields if they have valid values
        if (eventType && typeof eventType === 'string') {
          createData.eventType = eventType.trim();
        }
        if (eventPeriod != null && Number.isInteger(eventPeriod) && eventPeriod > 0) {
          // Format period using periodNamingMapping (e.g., 201 -> "P 1", 601 -> "OT 1")
          createData.period = decodePeriodDisplay(
            eventPeriod,
            periodNamingMapping,
            gameContext?.game?.season?.seasonType,
          ).abbreviated;
        }
        if (eventGameTime != null && typeof eventGameTime === 'number' && !isNaN(eventGameTime)) {
          // Format game time as "MM:SS" string (e.g., 754 seconds -> "12:34")
          createData.gameTime = secondsToTimeString(eventGameTime);
        }
        // Carry over a ratio picked before the tracked object (aspect-ratio
        // changes alone no longer persist the draft), matching the
        // first-clip persistence path in createClipInGroup.
        if (draftGroup.outputMode) {
          createData.outputMode = draftGroup.outputMode;
        }

        const newClipGroup = await createClipGroupMutation.mutateAsync({
          playbyplayId: selectedEventId,
          data: createData as any,
        });

        // Store the persisted ID but keep the draft visible until refetch completes
        setDraftClipGroups((prev) =>
          prev.map((group) =>
            group.id === draftId
              ? { ...group, isCreating: false, persistedId: newClipGroup.id }
              : group,
          ),
        );
      } catch (error: any) {
        // Clear loading state on error
        setDraftClipGroups((prev) =>
          prev.map((group) => (group.id === draftId ? { ...group, isCreating: false } : group)),
        );
        enqueueSnackbar(`Error creating clip group: ${error.message}`, { variant: 'error' });
      }
    },
    [
      selectedEventId,
      draftClipGroups,
      createClipGroupMutation,
      enqueueSnackbar,
      eventType,
      eventPeriod,
      eventGameTime,
      periodNamingMapping,
    ],
  );

  // Update tracked object (handles both drafts and persisted clipGroups)
  const updateTrackedObject = useCallback(
    async (groupId: string | number, trackedObjectId: string | null) => {
      // If it's a draft, delegate to updateDraftTrackedObject
      if (typeof groupId === 'string' && groupId.startsWith('draft-')) {
        return updateDraftTrackedObject(groupId, trackedObjectId);
      }

      // Handle persisted clipGroups
      if (!selectedEventId) {
        console.warn('Cannot update tracked object: missing selectedEventId');
        return;
      }

      const persistedGroup = persistedClipGroups.find((g) => g.id === groupId);
      if (!persistedGroup) {
        console.warn('Persisted clipGroup not found:', groupId);
        return;
      }

      Tracking.getInstance().track('Update Tracked Object', { category: 'Clips', draft: false });

      const hasClips = persistedGroup.clips && persistedGroup.clips.length > 0;

      try {
        if (hasClips) {
          // Reset clips first (they're invalid with new tracked object)
          const result = await resetClipGroupMutation.mutateAsync({
            playbyplayId: selectedEventId,
            clipGroupId: groupId as number,
            softDelete: true,
          });

          if (result.clipsReset > 0) {
            enqueueSnackbar(
              `${result.clipsReset} clip${result.clipsReset !== 1 ? 's' : ''} reset due to tracked object change`,
              // @ts-expect-error - custom variant
              { variant: 'clipReset' },
            );
          }
        }

        // Update the tracked object
        await updateClipGroupMutation.mutateAsync({
          playbyplayId: selectedEventId,
          clipGroupId: groupId as number,
          data: {
            trackedObjectId: trackedObjectId || undefined,
          },
        });

        // Force refetch to get updated data from backend
        await refetch();
      } catch (error: any) {
        enqueueSnackbar(`Failed to update tracked object: ${error.message}`, { variant: 'error' });
      }
    },
    [
      selectedEventId,
      persistedClipGroups,
      updateDraftTrackedObject,
      resetClipGroupMutation,
      updateClipGroupMutation,
      refetch,
      enqueueSnackbar,
    ],
  );

  // Update output mode (transcoding profile) for a draft clipGroup. Keep a new
  // draft local until the user selects a tracked object or creates a clip;
  // persisting the aspect ratio alone would create an unusable empty group.
  const updateDraftOutputMode = useCallback(
    async (draftId: string, outputMode: 'portrait' | 'landscape') => {
      // Update local draft state
      setDraftClipGroups((prev) =>
        prev.map((group) => (group.id === draftId ? { ...group, outputMode } : group)),
      );

      // If draft is already persisted, PATCH the API to update outputMode
      const draft = draftClipGroups.find((g) => g.id === draftId);
      if (draft?.persistedId && selectedEventId && gameContext?.game?.id) {
        try {
          const playbyplayId = (selectedEvent as any)?.customEventId || (selectedEvent as any)?.id;
          if (!playbyplayId) return;

          await patch<{ success: boolean; clipGroup: any }>(
            `/v1.0/games/${gameContext.game.id}/play-by-play/${playbyplayId}/clip-groups/${draft.persistedId}`,
            { outputMode },
          );
        } catch (error) {
          console.error('Error updating output mode:', error);
        }
        return;
      }
    },
    [draftClipGroups, selectedEventId, selectedEvent, gameContext],
  );

  // Create clip in group. If invoked on a draft (typical for the shift-download
  // flow with persistOnExportOnly=true, or any draft that hasn't auto-persisted
  // yet), the draft is persisted to the backend FIRST, then the clip is added
  // against the freshly-minted persisted id. This is the "persist on first
  // export" trigger — no backend state exists for this clipGroup until the
  // user actually picks a camera.
  const createClipInGroup = useCallback(
    async (groupId: string | number, camera: ClipCameraSelection): Promise<ClipCreationOutcome> => {
      if (!selectedEventId) return 'aborted';
      const isAutoDirector = camera === AUTO_DIRECTOR_CAMERA;

      let resolvedGroupId: number;

      if (typeof groupId === 'string' && groupId.startsWith('draft-')) {
        const draftGroup = draftClipGroups.find((g) => g.id === groupId);
        if (!draftGroup) {
          console.warn('Draft clipGroup not found:', groupId);
          return 'aborted';
        }
        if (!draftGroup.trackedObjectId) {
          enqueueSnackbar('Please select an object to track, then try again.', {
            variant: 'warning',
          });
          return 'aborted';
        }

        if (draftGroup.persistedId) {
          resolvedGroupId = draftGroup.persistedId;
        } else {
          setDraftClipGroups((prev) =>
            prev.map((g) => (g.id === groupId ? { ...g, isCreating: true } : g)),
          );

          try {
            const createData: Record<string, unknown> = {
              name: draftGroup.name?.trim() || 'Unnamed Event',
              trackedObjectId: draftGroup.trackedObjectId,
              inTime: draftGroup.inTime.toISOString(),
              outTime: draftGroup.outTime.toISOString(),
            };
            if (eventType && typeof eventType === 'string') {
              createData.eventType = eventType.trim();
            }
            if (eventPeriod != null && Number.isInteger(eventPeriod) && eventPeriod > 0) {
              createData.period = decodePeriodDisplay(
                eventPeriod,
                periodNamingMapping,
                gameContext?.game?.season?.seasonType,
              ).abbreviated;
            }
            if (
              eventGameTime != null &&
              typeof eventGameTime === 'number' &&
              !isNaN(eventGameTime)
            ) {
              createData.gameTime = secondsToTimeString(eventGameTime);
            }
            if (draftGroup.outputMode) {
              createData.outputMode = draftGroup.outputMode;
            }

            const newClipGroup = await createClipGroupMutation.mutateAsync({
              playbyplayId: selectedEventId,
              data: createData as any,
            });

            setDraftClipGroups((prev) =>
              prev.map((g) =>
                g.id === groupId ? { ...g, isCreating: false, persistedId: newClipGroup.id } : g,
              ),
            );
            resolvedGroupId = newClipGroup.id;
          } catch (error: any) {
            setDraftClipGroups((prev) =>
              prev.map((g) => (g.id === groupId ? { ...g, isCreating: false } : g)),
            );
            enqueueSnackbar(`Error creating clip group: ${error.message}`, { variant: 'error' });
            return 'aborted';
          }
        }
      } else {
        resolvedGroupId = groupId as number;
      }

      // If a retriable clip (NEW after a time reset, or ERROR/CANCELED from a
      // failed/aborted export) already exists for this camera, re-enqueue it
      // for export instead of creating a duplicate.
      const group = persistedClipGroups.find((g) => g.id === resolvedGroupId);
      // A just-persisted draft may not be in persistedClipGroups yet (the
      // invalidated query is still refetching) while ClipGroupItem already
      // addresses it by its numeric persistedId — fall back to that draft.
      const selectedTrackedObjectId =
        typeof groupId === 'string' && groupId.startsWith('draft-')
          ? draftClipGroups.find((g) => g.id === groupId)?.trackedObjectId
          : (group?.trackedObjectId ??
            draftClipGroups.find((g) => g.persistedId === resolvedGroupId)?.trackedObjectId);

      if (!selectedTrackedObjectId) {
        enqueueSnackbar('Please select an object to track, then try again.', {
          variant: 'warning',
        });
        return 'aborted';
      }

      // Only the tab that asked for an export auto-downloads it when it lands.
      markExportRequested(resolvedGroupId, String(camera));

      const retriableStatuses: string[] = [
        ClipStatus.NEW,
        ClipStatus.ERROR,
        ClipStatus.CANCELED,
        ClipStatus.CANCELING,
      ];
      const existingRetriableClip = group?.clips?.find(
        (c) =>
          (isAutoDirector ? c.autoDirector === true : !c.autoDirector && c.cameraId === camera) &&
          retriableStatuses.includes((c.status ?? '').toUpperCase()),
      );

      if (existingRetriableClip?.id) {
        try {
          await enqueueClipForExportMutation.mutateAsync(existingRetriableClip.id as number);
          Tracking.getInstance().track('Requeue Clip In ClipGroup', {
            category: 'Clips',
            autoDirector: isAutoDirector,
          });
          setTimeout(() => refetch(), 100);
          return 'requeued';
        } catch (error: any) {
          enqueueSnackbar(`Error re-queuing clip: ${error.message}`, { variant: 'error' });
          return 'aborted';
        }
      }

      try {
        // Add clip to the persisted clipGroup
        // Backend will automatically queue the clip for export
        await addClipToGroupMutation.mutateAsync({
          playbyplayId: selectedEventId,
          clipGroupId: resolvedGroupId,
          data: isAutoDirector
            ? { autoDirector: true, cameraMode: 'tracked' }
            : { cameraId: camera, cameraMode: 'tracked' },
        });
        Tracking.getInstance().track('Create Clip In ClipGroup', {
          category: 'Clips',
          autoDirector: isAutoDirector,
        });

        // Small delay to ensure backend has processed the clip before refetching
        setTimeout(() => refetch(), 100);
        return 'created';
      } catch (error: any) {
        console.error('Error creating clip:', error);
        enqueueSnackbar(`Error adding clip: ${error.message}`, { variant: 'error' });
        return 'aborted';
      }
    },
    [
      selectedEventId,
      draftClipGroups,
      persistedClipGroups,
      createClipGroupMutation,
      addClipToGroupMutation,
      enqueueClipForExportMutation,
      enqueueSnackbar,
      refetch,
      eventType,
      eventPeriod,
      eventGameTime,
      periodNamingMapping,
      gameContext?.game?.season?.seasonType,
    ],
  );

  // Delete clipGroup
  const deleteClipGroup = useCallback(
    async (groupId: string | number) => {
      if (!selectedEventId) return;

      // Check if it's a draft (just remove from local state)
      if (typeof groupId === 'string' && groupId.startsWith('draft-')) {
        setDraftClipGroups((prev) => prev.filter((g) => g.id !== groupId));
        return;
      }

      // Delete persisted clipGroup
      try {
        await deleteClipGroupMutation.mutateAsync({
          playbyplayId: selectedEventId,
          clipGroupId: groupId as number,
        });
        Tracking.getInstance().track('Delete ClipGroup', { category: 'Clips' });

        // Explicitly refetch to ensure UI updates immediately
        await refetch();
      } catch (error: any) {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      }
    },
    [selectedEventId, deleteClipGroupMutation, refetch, enqueueSnackbar],
  );

  // Refetch clip groups from backend
  const refetchClipGroups = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Reset all state (useful when closing drawer)
  const reset = useCallback(() => {
    setSelectedEventState(null);
    setDraftClipGroups([]);
  }, []);

  // Save fixed camera clip to backend (creates or updates clip with position)
  const saveFixedClip = useCallback(
    async (clipGroupId: number, data: FixedClipData) => {
      if (!selectedEventId) {
        enqueueSnackbar('No event selected', { variant: 'error' });
        return false;
      }

      setIsSavingFixedClip(true);
      Tracking.getInstance().track('Save Fixed Camera Position', { category: 'Clips' });

      try {
        await addClipToGroupMutation.mutateAsync({
          playbyplayId: selectedEventId,
          clipGroupId,
          data: {
            cameraId: data.cameraId,
            cameraMode: 'fixed',
            pan: data.pan,
            tilt: data.tilt,
            zoom: data.zoom,
            framingPresetId: data.framingPresetId,
          },
        });

        await refetch();
        enqueueSnackbar('Position saved', { variant: 'success' });
        return true;
      } catch (error: any) {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
        return false;
      } finally {
        setIsSavingFixedClip(false);
      }
    },
    [selectedEventId, addClipToGroupMutation, refetch, enqueueSnackbar],
  );

  // Trigger export for an existing clip
  const triggerFixedClipExport = useCallback(
    async (clipId: number) => {
      Tracking.getInstance().track('Export Fixed Camera Clip', { category: 'Clips' });

      try {
        await enqueueClipForExportMutation.mutateAsync(clipId);
        await refetch();
        enqueueSnackbar('Export started', { variant: 'success' });
      } catch (error: any) {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      }
    },
    [enqueueClipForExportMutation, refetch, enqueueSnackbar],
  );

  const value: MediaContextValue = {
    // State
    clipGroups,
    selectedEventId,
    isLoading,
    isSavingFixedClip,

    // Actions
    setSelectedEvent,
    updateClipGroupTime,
    addDraftClipGroup,
    updateDraftTrackedObject,
    updateTrackedObject,
    updateDraftOutputMode,
    createClipInGroup,
    deleteClipGroup,
    refetchClipGroups,
    reset,

    // Fixed camera clip actions
    saveFixedClip,
    triggerFixedClipExport,
  };

  return <MediaContext.Provider value={value}>{children}</MediaContext.Provider>;
};

// Custom hook to use MediaContext
export const useMediaContext = () => {
  const context = useContext(MediaContext);
  if (context === undefined) {
    throw new Error('useMediaContext must be used within a MediaProvider');
  }
  return context;
};

export default MediaContext;
